import React, { useState, useRef } from "react";
import { generateClient } from "aws-amplify/api";
import { CreatePdfUploadUrlInput } from "../models/API";
import { createPdfUploadUrl } from "../graphql/mutations";
import { IconButton, Tooltip, Typography, Box } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface PdfUploadButtonProps {
  userId: number;
  onPdfUploaded: (s3Key: string, filename: string) => void;
  onPdfRemoved: () => void;
  disabled?: boolean;
}

const PdfUploadButton: React.FC<PdfUploadButtonProps> = ({
  userId,
  onPdfUploaded,
  onPdfRemoved,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const client = generateClient();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (file.type !== "application/pdf") {
      alert("Please select a PDF file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert("File size must be less than 10MB.");
      return;
    }

    setUploading(true);
    try {
      const input: CreatePdfUploadUrlInput = {
        user_id: userId,
        file_name: file.name,
        content_type: "application/pdf",
      };

      const result = await client.graphql({
        query: createPdfUploadUrl,
        variables: { input },
      });

      const { upload_url, file_key } = result.data.createPdfUploadUrl!;

      await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": "application/pdf" },
        body: file,
      });

      setUploadedFilename(file.name);
      onPdfUploaded(file_key, file.name);
    } catch (err) {
      console.error("PDF upload failed:", err);
      alert("Failed to upload PDF. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setUploadedFilename(null);
    onPdfRemoved();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
      {uploadedFilename ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>
            {uploadedFilename}
          </Typography>
          <IconButton size="small" onClick={handleRemove} disabled={disabled}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Tooltip title="Attach PDF">
          <span>
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading}
            >
              {uploading ? (
                <CircularProgress size={18} />
              ) : (
                <AttachFileIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default PdfUploadButton;
